import React, { useEffect, useState } from 'react';
import { hrService, subcontractorService } from '../../services';
import {
  Search, Plus, X, Users, BookOpen, BarChart2,
  Briefcase, Star, Edit2, Download, Activity, UserX
} from 'lucide-react';

const DEPT_COLORS: Record<string,string> = {
  MANAGEMENT:'bg-purple-100 text-purple-700', CIVIL:'bg-blue-100 text-blue-700',
  MEP:'bg-cyan-100 text-cyan-700', ACCOUNTS:'bg-emerald-100 text-emerald-700',
  PURCHASE:'bg-amber-100 text-amber-700', ADMIN:'bg-gray-100 text-gray-700',
  HR:'bg-pink-100 text-pink-700', SITE:'bg-orange-100 text-orange-700',
};
const TRADE_COLORS: Record<string,string> = {
  CIVIL:'bg-blue-100 text-blue-700', STRUCTURAL:'bg-indigo-100 text-indigo-700',
  MEP:'bg-cyan-100 text-cyan-700', FINISHING:'bg-rose-100 text-rose-700',
  ADMIN:'bg-gray-100 text-gray-700', SAFETY:'bg-amber-100 text-amber-700',
};
const DEPARTMENTS   = ['MANAGEMENT','CIVIL','MEP','ACCOUNTS','PURCHASE','HR','ADMIN','SITE'];
const ROLES         = ['ADMIN','PM','SITE_ENG','ACCOUNTS','PURCHASE','ESTIMATOR'];
const EMP_TYPES     = ['PERMANENT','CONTRACT','PROBATION','INTERN'];
const TRADE_CATS    = ['CIVIL','MEP','FINISHING','STRUCTURAL','ADMIN','SAFETY'];
const SKILL_LEVELS  = ['TRAINEE','STANDARD','SENIOR','HEAD'];
const SPECIALISATIONS = [
  'CIVIL','MASONRY','PLASTERING','WATERPROOFING','FLOORING','PAINTING',
  'CARPENTRY','ELECTRICAL','PLUMBING','MEP','FABRICATION','LANDSCAPING','OTHER'
];
const SPEC_COLORS: Record<string,string> = {
  CIVIL:'bg-blue-100 text-blue-700', MASONRY:'bg-stone-100 text-stone-700',
  PLASTERING:'bg-yellow-100 text-yellow-700', PAINTING:'bg-pink-100 text-pink-700',
  ELECTRICAL:'bg-amber-100 text-amber-700', PLUMBING:'bg-cyan-100 text-cyan-700',
  MEP:'bg-teal-100 text-teal-700', FLOORING:'bg-orange-100 text-orange-700',
  WATERPROOFING:'bg-indigo-100 text-indigo-700', CARPENTRY:'bg-lime-100 text-lime-700',
  FABRICATION:'bg-gray-100 text-gray-700', LANDSCAPING:'bg-green-100 text-green-700',
  OTHER:'bg-gray-100 text-gray-500',
};
const inp   = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const inpRO = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-400 cursor-not-allowed";
const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-IN') : '—';
const fmtN = (n: any) => n ? Number(n).toLocaleString() : '0';
const exportCSV = (data: any[], filename: string) => {
  if (!data.length) return;
  const keys = Object.keys(data[0]);
  const csv = [keys.join(','), ...data.map(row =>
    keys.map(k => `"${String(row[k]??'').replace(/"/g,'""')}"`).join(',')
  )].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
  a.download = filename; a.click();
};
const StarRating = ({ value, onChange, readonly=false }: {
  value: number; onChange?: (v:number)=>void; readonly?: boolean;
}) => (
  <div className="flex gap-0.5">
    {[1,2,3,4,5].map(s => (
      <button key={s} type="button" onClick={() => !readonly && onChange?.(s)}
        className={readonly ? 'cursor-default' : 'cursor-pointer'}>
        <Star size={13} className={s<=value ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}/>
      </button>
    ))}
  </div>
);

// ── Employee Modal ────────────────────────────────────────────
const EmployeeModal = ({ employee, onClose, onSaved }: {
  employee?: any; onClose: ()=>void; onSaved: ()=>void;
}) => {
  const isEdit = !!employee;
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [subtab, setSubtab]       = useState<'basic'|'personal'|'bank'>('basic');
  const [employees, setEmployees] = useState<any[]>([]);
  const [form, setForm] = useState({
    full_name:          employee?.full_name          || '',
    designation:        employee?.designation        || '',
    department:         employee?.department         || 'CIVIL',
    role_code:          employee?.role_code          || 'SITE_ENG',
    mobile:             employee?.mobile             || '',
    email:              employee?.email              || '',
    date_of_joining:    employee?.date_of_joining?.split('T')[0] || new Date().toISOString().split('T')[0],
    employment_type:    employee?.employment_type    || 'PERMANENT',
    reporting_to:       employee?.reporting_to       || '',
    address:            employee?.address            || '',
    emergency_contact:  employee?.emergency_contact  || '',
    emergency_mobile:   employee?.emergency_mobile   || '',
    pan_number:         employee?.pan_number         || '',
    aadhaar_number_enc: employee?.aadhaar_number_enc || '',
    pf_account_number:  employee?.pf_account_number  || '',
    esi_number:         employee?.esi_number         || '',
    bank_account:       employee?.bank_account       || '',
    bank_ifsc:          employee?.bank_ifsc          || '',
    bank_name:          employee?.bank_name          || '',
    is_active:          employee?.is_active !== undefined ? employee.is_active : true,
  });
  const set = (k:string, v:any) => setForm(f=>({...f,[k]:v}));
  useEffect(() => { hrService.getEmployees().then(r => setEmployees(r.data.data)); }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      const payload = { ...form, reporting_to: form.reporting_to ? Number(form.reporting_to) : null };
      if (isEdit) { await api.put(`/hr/employees/${employee.employee_id}`, payload); }
      else        { await api.post('/hr/employees', payload); }
      onSaved();
    } catch(err:any) { setError(err.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <div>
            <h3 className="font-bold text-gray-900">{isEdit ? 'Edit Employee' : 'Add Employee'}</h3>
            {isEdit && <p className="text-xs text-gray-400">{employee.employee_code}</p>}
          </div>
          <button onClick={onClose}><X size={18} className="text-gray-400"/></button>
        </div>
        <div className="flex gap-1 px-6 pt-4">
          {(['basic','personal','bank'] as const).map(t => (
            <button key={t} type="button" onClick={() => setSubtab(t)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors
                ${subtab===t ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {t==='basic' ? 'Basic Info' : t==='personal' ? 'Statutory' : 'Bank'}
            </button>
          ))}
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}
          {subtab === 'basic' && <>
            {!isEdit && <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">Code auto-generated (e.g. EMP-009)</div>}
            {isEdit && <><label className="text-xs font-medium text-gray-600 block mb-1">Employee Code</label><input value={employee.employee_code} readOnly className={inpRO}/></>}
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Full Name *</label>
                <input required value={form.full_name} onChange={e=>set('full_name',e.target.value)} placeholder="Rajesh Kumar" className={inp}/></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Designation *</label>
                <input required value={form.designation} onChange={e=>set('designation',e.target.value)} placeholder="Site Engineer" className={inp}/></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Department *</label>
                <select required value={form.department} onChange={e=>set('department',e.target.value)} className={inp}>
                  {DEPARTMENTS.map(d=><option key={d}>{d}</option>)}</select></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Role *</label>
                <select required value={form.role_code} onChange={e=>set('role_code',e.target.value)} className={inp}>
                  {ROLES.map(r=><option key={r}>{r}</option>)}</select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Mobile *</label>
                <input required value={form.mobile} onChange={e=>set('mobile',e.target.value)} placeholder="9876543210" className={inp}/></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Email</label>
                <input type="email" value={form.email} onChange={e=>set('email',e.target.value)} className={inp}/></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Date of Joining *</label>
                <input required type="date" value={form.date_of_joining} onChange={e=>set('date_of_joining',e.target.value)} className={inp}/></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Employment Type</label>
                <select value={form.employment_type} onChange={e=>set('employment_type',e.target.value)} className={inp}>
                  {EMP_TYPES.map(t=><option key={t}>{t}</option>)}</select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Reports To</label>
                <select value={form.reporting_to} onChange={e=>set('reporting_to',e.target.value)} className={inp}>
                  <option value="">None</option>
                  {employees.filter(e=>!isEdit||e.employee_id!==employee?.employee_id).map(emp=>(
                    <option key={emp.employee_id} value={emp.employee_id}>{emp.full_name}</option>
                  ))}</select></div>
              {isEdit && <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_active} onChange={e=>set('is_active',e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-blue-600"/>
                  <span className="text-sm text-gray-700">Active Employee</span></label></div>}
            </div>
            <div><label className="text-xs font-medium text-gray-600 block mb-1">Address</label>
              <textarea rows={2} value={form.address} onChange={e=>set('address',e.target.value)} className={inp}/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Emergency Contact</label>
                <input value={form.emergency_contact} onChange={e=>set('emergency_contact',e.target.value)} className={inp}/></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Emergency Mobile</label>
                <input value={form.emergency_mobile} onChange={e=>set('emergency_mobile',e.target.value)} className={inp}/></div>
            </div>
          </>}
          {subtab === 'personal' && <>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">Statutory details — handle with care.</div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">PAN Number</label>
                <input value={form.pan_number} onChange={e=>set('pan_number',e.target.value)} placeholder="ABCDE1234F" className={inp}/></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Aadhaar (last 4)</label>
                <input value={form.aadhaar_number_enc} onChange={e=>set('aadhaar_number_enc',e.target.value)} placeholder="XXXX" className={inp}/></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">PF Account Number</label>
                <input value={form.pf_account_number} onChange={e=>set('pf_account_number',e.target.value)} placeholder="KN/BN/0000000/000" className={inp}/></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">ESI Number</label>
                <input value={form.esi_number} onChange={e=>set('esi_number',e.target.value)} className={inp}/></div>
            </div>
          </>}
          {subtab === 'bank' && <>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">Bank details for salary transfer. Keep confidential.</div>
            <div><label className="text-xs font-medium text-gray-600 block mb-1">Bank Name</label>
              <input value={form.bank_name} onChange={e=>set('bank_name',e.target.value)} placeholder="State Bank of India" className={inp}/></div>
            <div><label className="text-xs font-medium text-gray-600 block mb-1">Account Number</label>
              <input value={form.bank_account} onChange={e=>set('bank_account',e.target.value)} className={inp}/></div>
            <div><label className="text-xs font-medium text-gray-600 block mb-1">IFSC Code</label>
              <input value={form.bank_ifsc} onChange={e=>set('bank_ifsc',e.target.value)} placeholder="SBIN0001234" className={inp}/></div>
          </>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : (isEdit ? 'Update Employee' : 'Add Employee')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Skill Modal ───────────────────────────────────────────────
const SkillModal = ({ skill, onClose, onSaved }: {
  skill?: any; onClose: ()=>void; onSaved: ()=>void;
}) => {
  const isEdit = !!skill;
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [form, setForm] = useState({
    skill_name:          skill?.skill_name          || '',
    trade_category:      skill?.trade_category      || 'CIVIL',
    skill_level:         skill?.skill_level         || 'STANDARD',
    is_supervisory:      skill?.is_supervisory      || false,
    current_daily_rate:  skill?.current_daily_rate  || '',
    rate_effective_from: skill?.rate_effective_from?.split('T')[0] || '',
    is_active:           skill?.is_active !== undefined ? skill.is_active : true,
  });
  const set = (k:string, v:any) => setForm(f=>({...f,[k]:v}));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      if (isEdit) { await api.put(`/hr/skills/${skill.skill_id}`, form); }
      else        { await api.post('/hr/skills', { ...form, current_daily_rate: form.current_daily_rate||null }); }
      onSaved();
    } catch(err:any) { setError(err.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="font-bold text-gray-900">{isEdit ? 'Edit Skill' : 'Add Skill'}</h3>
            {isEdit && <p className="text-xs text-gray-400">{skill.skill_code}</p>}
          </div>
          <button onClick={onClose}><X size={18} className="text-gray-400"/></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}
          {isEdit
            ? <div><label className="text-xs font-medium text-gray-600 block mb-1">Skill Code</label><input value={skill.skill_code} readOnly className={inpRO}/></div>
            : <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">Code auto-generated (e.g. SKL-CIV-001)</div>}
          <div><label className="text-xs font-medium text-gray-600 block mb-1">Skill Name *</label>
            <input required value={form.skill_name} onChange={e=>set('skill_name',e.target.value)} placeholder="Mason, Carpenter..." className={inp}/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-gray-600 block mb-1">Trade Category *</label>
              <select value={form.trade_category} onChange={e=>set('trade_category',e.target.value)} className={inp}>
                {TRADE_CATS.map(c=><option key={c}>{c}</option>)}</select></div>
            <div><label className="text-xs font-medium text-gray-600 block mb-1">Skill Level</label>
              <select value={form.skill_level} onChange={e=>set('skill_level',e.target.value)} className={inp}>
                {SKILL_LEVELS.map(l=><option key={l}>{l}</option>)}</select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-gray-600 block mb-1">Daily Rate (₹)</label>
              <input type="number" min={0} value={form.current_daily_rate} onChange={e=>set('current_daily_rate',e.target.value)} placeholder="900" className={inp}/></div>
            <div><label className="text-xs font-medium text-gray-600 block mb-1">Rate Effective From</label>
              <input type="date" value={form.rate_effective_from} onChange={e=>set('rate_effective_from',e.target.value)} className={inp}/></div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_supervisory} onChange={e=>set('is_supervisory',e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-blue-600"/>
              <span className="text-sm text-gray-700">Supervisory role</span></label>
            {isEdit && <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={e=>set('is_active',e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-blue-600"/>
              <span className="text-sm text-gray-700">Active</span></label>}
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : (isEdit ? 'Update Skill' : 'Add Skill')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Subcontractor Modal ───────────────────────────────────────
const SubcontractorModal = ({ sub, onClose, onSaved }: {
  sub?: any; onClose: ()=>void; onSaved: ()=>void;
}) => {
  const isEdit = !!sub;
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [specs, setSpecs]   = useState<string[]>(sub?.specialisation || []);
  const [subtab, setSubtab] = useState<'basic'|'bank'>('basic');
  const [form, setForm] = useState({
    company_name:   sub?.company_name   || '',
    contact_person: sub?.contact_person || '',
    mobile:         sub?.mobile         || '',
    gstin:          sub?.gstin          || '',
    pan_number:     sub?.pan_number     || '',
    service_areas:  sub?.service_areas  || 'Bengaluru',
    bank_account:   sub?.bank_account   || '',
    bank_ifsc:      sub?.bank_ifsc      || '',
    bank_name:      sub?.bank_name      || '',
    rating:         sub?.rating         || 0,
    is_active:      sub?.is_active !== undefined ? sub.is_active : true,
  });
  const set = (k:string, v:any) => setForm(f=>({...f,[k]:v}));
  const toggleSpec = (s:string) => setSpecs(p => p.includes(s) ? p.filter(x=>x!==s) : [...p,s]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const payload = { ...form, specialisation: specs, rating: form.rating||null };
      if (isEdit) { await subcontractorService.update(sub.sub_id, payload); }
      else        { await subcontractorService.create(payload); }
      onSaved();
    } catch(err:any) { setError(err.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <div>
            <h3 className="font-bold text-gray-900">{isEdit ? 'Edit Subcontractor' : 'Add Subcontractor'}</h3>
            {isEdit && <p className="text-xs text-gray-400">{sub.sub_code}</p>}
          </div>
          <button onClick={onClose}><X size={18} className="text-gray-400"/></button>
        </div>
        <div className="flex gap-1 px-6 pt-4">
          {(['basic','bank'] as const).map(t => (
            <button key={t} type="button" onClick={() => setSubtab(t)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors
                ${subtab===t ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {t==='basic' ? 'Basic Info' : 'Bank Details'}</button>
          ))}
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}
          {subtab === 'basic' && <>
            {!isEdit && <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">Code auto-generated (e.g. SUB-001)</div>}
            {isEdit && <><label className="text-xs font-medium text-gray-600 block mb-1">Sub Code</label><input value={sub.sub_code} readOnly className={inpRO}/></>}
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Company Name *</label>
                <input required value={form.company_name} onChange={e=>set('company_name',e.target.value)} placeholder="Raju Plastering Works" className={inp}/></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Contact Person *</label>
                <input required value={form.contact_person} onChange={e=>set('contact_person',e.target.value)} className={inp}/></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Mobile *</label>
                <input required value={form.mobile} onChange={e=>set('mobile',e.target.value)} className={inp}/></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">GSTIN *</label>
                <input required value={form.gstin} onChange={e=>set('gstin',e.target.value)} placeholder="29XXXXX1234F1Z5" className={inp}/></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">PAN Number</label>
                <input value={form.pan_number} onChange={e=>set('pan_number',e.target.value)} className={inp}/></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Service Areas *</label>
                <input required value={form.service_areas} onChange={e=>set('service_areas',e.target.value)} className={inp}/></div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Specialisation * (select all that apply)</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {SPECIALISATIONS.map(s => (
                  <button key={s} type="button" onClick={()=>toggleSpec(s)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors
                      ${specs.includes(s) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}>
                    {s}</button>
                ))}
              </div>
            </div>
            <div><label className="text-xs font-medium text-gray-600 block mb-1">Rating</label>
              <StarRating value={form.rating} onChange={v=>set('rating',v)}/></div>
            {isEdit && <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={e=>set('is_active',e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-blue-600"/>
              <span className="text-sm text-gray-700">Active</span></label>}
          </>}
          {subtab === 'bank' && <>
            <div><label className="text-xs font-medium text-gray-600 block mb-1">Bank Name</label>
              <input value={form.bank_name} onChange={e=>set('bank_name',e.target.value)} className={inp}/></div>
            <div><label className="text-xs font-medium text-gray-600 block mb-1">Account Number</label>
              <input value={form.bank_account} onChange={e=>set('bank_account',e.target.value)} className={inp}/></div>
            <div><label className="text-xs font-medium text-gray-600 block mb-1">IFSC Code</label>
              <input value={form.bank_ifsc} onChange={e=>set('bank_ifsc',e.target.value)} className={inp}/></div>
          </>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving||(!isEdit&&specs.length===0)} className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : (isEdit ? 'Update' : 'Add Subcontractor')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Employees Tab ─────────────────────────────────────────────
const EmployeesTab = () => {
  const [employees, setEmployees] = useState<any[]>([]);
  const [stats, setStats]         = useState<any>(null);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [dept, setDept]           = useState('');
  const [showInactive, setInactive] = useState(false);
  const [showAdd, setShowAdd]     = useState(false);
  const [editEmp, setEditEmp]     = useState<any>(null);
  const [deactivating, setDeact]  = useState<number|null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [eRes, sRes] = await Promise.all([
        hrService.getEmployees({ department: dept||undefined, search: search||undefined, show_inactive: showInactive }),
        hrService.getStats(),
      ]);
      setEmployees(eRes.data.data); setStats(sRes.data.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [dept, showInactive]);

  const deactivate = async (emp: any) => {
    if (!window.confirm(`Deactivate ${emp.full_name}?`)) return;
    setDeact(emp.employee_id);
    try {
      const { default: api } = await import('../../utils/api');
      await api.patch(`/hr/employees/${emp.employee_id}/deactivate`, { date_of_leaving: new Date().toISOString().split('T')[0] });
      load();
    } finally { setDeact(null); }
  };

  const deptBgMap: Record<string,string> = {
    MANAGEMENT:'bg-purple-500', CIVIL:'bg-blue-500', MEP:'bg-cyan-500',
    ACCOUNTS:'bg-emerald-500', PURCHASE:'bg-amber-500', HR:'bg-pink-500',
    ADMIN:'bg-gray-500', SITE:'bg-orange-500',
  };

  return (
    <>
      {showAdd && <EmployeeModal onClose={()=>setShowAdd(false)} onSaved={()=>{ setShowAdd(false); load(); }}/>}
      {editEmp && <EmployeeModal employee={editEmp} onClose={()=>setEditEmp(null)} onSaved={()=>{ setEditEmp(null); load(); }}/>}

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-500">{stats ? `${fmtN(stats.total_active)} active employees` : ''}</p>
          <label className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-500">
            <input type="checkbox" checked={showInactive} onChange={e=>setInactive(e.target.checked)} className="w-3.5 h-3.5 rounded border-gray-300"/>
            Show inactive</label>
        </div>
        <div className="flex gap-2">
          <button onClick={()=>exportCSV(employees.map(e=>({
            Code:e.employee_code,Name:e.full_name,Designation:e.designation,
            Dept:e.department,Role:e.role_code,Mobile:e.mobile,
            Email:e.email||'',Type:e.employment_type,DOJ:fmtDate(e.date_of_joining),
            ReportsTo:e.reporting_to_name||'',Active:e.is_active?'Yes':'No'
          })),'employees.csv')}
            className="flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">
            <Download size={14}/> Export</button>
          <button onClick={()=>setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
            <Plus size={15}/> Add Employee</button>
        </div>
      </div>

      {stats?.by_department && (
        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={()=>setDept('')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
              ${!dept ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200'}`}>
            All ({stats.total_active})</button>
          {Object.entries(stats.by_department).map(([d,cnt]:any) => (
            <button key={d} onClick={()=>setDept(d===dept?'':d)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
                ${dept===d ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200'}`}>
              {d} ({cnt})</button>
          ))}
        </div>
      )}

      <form onSubmit={e=>{e.preventDefault();load();}} className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name / code / mobile..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        <button type="submit" className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">Search</button>
      </form>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_,i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full"/>
                <div className="flex-1"><div className="bg-gray-100 rounded h-4 w-3/4 mb-1"/><div className="bg-gray-100 rounded h-3 w-1/2"/></div>
              </div>
            </div>
          ))}
        </div>
      ) : employees.length === 0 ? (
        <div className="text-center py-12 text-gray-400">No employees found.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {employees.map(emp => {
            const initials = emp.full_name.split(' ').map((n:string)=>n[0]).join('').substring(0,2).toUpperCase();
            return (
              <div key={emp.employee_id}
                className={`bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm transition-shadow ${emp.is_active===false?'opacity-60':''}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white ${deptBgMap[emp.department]||'bg-gray-500'}`}>
                      {initials}</div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{emp.full_name}</p>
                      <p className="text-xs text-gray-400">{emp.employee_code}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${DEPT_COLORS[emp.department]||'bg-gray-100 text-gray-700'}`}>
                    {emp.department}</span>
                </div>
                <p className="text-sm text-gray-600 mb-1">{emp.designation}</p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  <span className="text-xs bg-gray-50 border border-gray-200 px-2 py-0.5 rounded text-gray-600">{emp.employment_type}</span>
                  <span className="text-xs bg-gray-50 border border-gray-200 px-2 py-0.5 rounded text-gray-600">{emp.role_code}</span>
                </div>
                <p className="text-xs text-gray-400">{emp.mobile}</p>
                {emp.email && <p className="text-xs text-gray-400">{emp.email}</p>}
                <p className="text-xs text-gray-300 mt-1">Joined: {fmtDate(emp.date_of_joining)}</p>
                {emp.reporting_to_name && <p className="text-xs text-gray-400 mt-0.5">↑ {emp.reporting_to_name}</p>}
                {emp.is_active===false && <p className="text-xs text-red-500 mt-1">Inactive · Left: {fmtDate(emp.date_of_leaving)}</p>}
                <div className="flex gap-2 mt-3">
                  <button onClick={()=>setEditEmp(emp)}
                    className="flex-1 flex items-center justify-center gap-1 text-xs text-blue-600 border border-blue-200 rounded-lg py-1.5 hover:bg-blue-50">
                    <Edit2 size={12}/> Edit</button>
                  {emp.is_active!==false && (
                    <button onClick={()=>deactivate(emp)} disabled={deactivating===emp.employee_id}
                      className="px-3 text-xs text-red-600 border border-red-200 rounded-lg py-1.5 hover:bg-red-50 disabled:opacity-50">
                      <UserX size={12}/></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

// ── Skills Tab ────────────────────────────────────────────────
const SkillsTab = () => {
  const [skills, setSkills]       = useState<any[]>([]);
  const [loading, setLoading]     = useState(true);
  const [tradeFilter, setTrade]   = useState('');
  const [levelFilter, setLevel]   = useState('');
  const [showAdd, setShowAdd]     = useState(false);
  const [editSkill, setEditSkill] = useState<any>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await hrService.getSkills({ trade: tradeFilter||undefined, level: levelFilter||undefined });
      setSkills(r.data.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tradeFilter, levelFilter]);

  return (
    <>
      {showAdd && <SkillModal onClose={()=>setShowAdd(false)} onSaved={()=>{ setShowAdd(false); load(); }}/>}
      {editSkill && <SkillModal skill={editSkill} onClose={()=>setEditSkill(null)} onSaved={()=>{ setEditSkill(null); load(); }}/>}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{skills.length} skills / labour categories</p>
        <div className="flex gap-2">
          <button onClick={()=>exportCSV(skills.map(s=>({
            Code:s.skill_code,Name:s.skill_name,Trade:s.trade_category,
            Level:s.skill_level,Supervisory:s.is_supervisory?'Yes':'No',
            DailyRate:s.current_daily_rate||'',RateFrom:fmtDate(s.rate_effective_from)
          })),'skills.csv')}
            className="flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">
            <Download size={14}/> Export</button>
          <button onClick={()=>setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
            <Plus size={15}/> Add Skill</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {['',...TRADE_CATS].map(c=>(
          <button key={c} onClick={()=>setTrade(c)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
              ${tradeFilter===c?'bg-blue-600 text-white border-blue-600':'bg-white text-gray-600 border-gray-200'}`}>
            {c||'All Trades'}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {['',...SKILL_LEVELS].map(l=>(
          <button key={l} onClick={()=>setLevel(l)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
              ${levelFilter===l?'bg-purple-600 text-white border-purple-600':'bg-white text-gray-600 border-gray-200'}`}>
            {l||'All Levels'}</button>
        ))}
      </div>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Skill</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Trade</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Level</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Daily Rate</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Effective</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? [...Array(5)].map((_,i)=>(
              <tr key={i}>{[...Array(7)].map((_,j)=>(
                <td key={j} className="px-4 py-3"><div className="bg-gray-100 rounded h-4 animate-pulse"/></td>
              ))}</tr>
            )) : skills.map(s=>(
              <tr key={s.skill_id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{s.skill_name}</div>
                  <div className="text-xs text-gray-400">{s.skill_code}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TRADE_COLORS[s.trade_category]||'bg-gray-100 text-gray-700'}`}>
                    {s.trade_category}</span>
                </td>
                <td className="px-4 py-3 text-gray-600 text-xs">{s.skill_level}</td>
                <td className="px-4 py-3">
                  {s.current_daily_rate
                    ? <span className="font-semibold text-gray-900">₹{fmtN(s.current_daily_rate)}/day</span>
                    : <span className="text-gray-400">—</span>}
                </td>
                <td className="px-4 py-3 text-gray-400 text-xs">{fmtDate(s.rate_effective_from)}</td>
                <td className="px-4 py-3">
                  {s.is_supervisory && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Supervisory</span>}
                </td>
                <td className="px-4 py-3">
                  <button onClick={()=>setEditSkill(s)}
                    className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-lg hover:bg-blue-100">
                    <Edit2 size={11}/> Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && skills.length===0 && <div className="text-center py-12 text-gray-400">No skills found.</div>}
      </div>
    </>
  );
};

// ── Subcontractors Tab ────────────────────────────────────────
const SubcontractorsTab = () => {
  const [subs, setSubs]       = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editSub, setEditSub] = useState<any>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await subcontractorService.getAll({ search: search||undefined });
      setSubs(r.data.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <>
      {showAdd && <SubcontractorModal onClose={()=>setShowAdd(false)} onSaved={()=>{ setShowAdd(false); load(); }}/>}
      {editSub && <SubcontractorModal sub={editSub} onClose={()=>setEditSub(null)} onSaved={()=>{ setEditSub(null); load(); }}/>}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{subs.length} subcontractors</p>
        <div className="flex gap-2">
          <button onClick={()=>exportCSV(subs.map(s=>({
            Code:s.sub_code,Company:s.company_name,Contact:s.contact_person,
            Mobile:s.mobile,GSTIN:s.gstin,Areas:s.service_areas,
            Specs:(s.specialisation||[]).join('|'),Rating:s.rating||''
          })),'subcontractors.csv')}
            className="flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">
            <Download size={14}/> Export</button>
          <button onClick={()=>setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
            <Plus size={15}/> Add Subcontractor</button>
        </div>
      </div>
      <form onSubmit={e=>{e.preventDefault();load();}} className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search subcontractors..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        <button type="submit" className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">Search</button>
      </form>
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_,i)=>(
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
              <div className="bg-gray-100 rounded h-5 w-2/3 mb-2"/><div className="bg-gray-100 rounded h-4 w-1/2"/>
            </div>
          ))}
        </div>
      ) : subs.length===0 ? (
        <div className="text-center py-16 text-gray-400">
          <Briefcase size={40} className="mx-auto mb-3 opacity-30"/>
          <p>No subcontractors added yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {subs.map(s=>(
            <div key={s.sub_id} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-semibold text-gray-900">{s.company_name}</p>
                  <p className="text-xs text-gray-400">{s.sub_code}</p>
                </div>
                <StarRating value={s.rating||0} readonly/>
              </div>
              <div className="text-sm text-gray-600 mb-1">{s.contact_person} · {s.mobile}</div>
              <div className="text-xs text-gray-400 mb-2">{s.service_areas}</div>
              {s.specialisation && s.specialisation.length>0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {s.specialisation.map((sp:string)=>(
                    <span key={sp} className={`text-xs px-2 py-0.5 rounded-full font-medium ${SPEC_COLORS[sp]||'bg-gray-100 text-gray-600'}`}>{sp}</span>
                  ))}
                </div>
              )}
              {s.gstin && <p className="text-xs text-gray-400">GSTIN: {s.gstin}</p>}
              <div className="flex gap-2 mt-3">
                <button onClick={()=>setEditSub(s)}
                  className="flex-1 flex items-center justify-center gap-1 text-xs text-blue-600 border border-blue-200 rounded-lg py-1.5 hover:bg-blue-50">
                  <Edit2 size={12}/> Edit</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// ── Manpower Mapping Tab ──────────────────────────────────────
const ManpowerMappingTab = () => {
  const [mappings, setMappings]   = useState<any[]>([]);
  const [filtered, setFiltered]   = useState<any[]>([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [tradeFilter, setTrade]   = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { default: api } = await import('../../utils/api');
        const r = await api.get('/hr/manpower-mappings');
        setMappings(r.data.data||[]); setFiltered(r.data.data||[]);
      } catch { setMappings([]); setFiltered([]); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  useEffect(() => {
    let data = mappings;
    if (tradeFilter) data = data.filter((m:any)=>m.trade_category===tradeFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter((m:any)=>
        m.activity_name?.toLowerCase().includes(q) ||
        m.act_code?.toLowerCase().includes(q) ||
        m.skill_name?.toLowerCase().includes(q) ||
        m.skill_code?.toLowerCase().includes(q)
      );
    }
    setFiltered(data);
  }, [search, tradeFilter, mappings]);

  const grouped = filtered.reduce((acc:any, row:any) => {
    const key = row.act_code;
    if (!acc[key]) acc[key] = { act_code:row.act_code, activity_name:row.activity_name, items:[] };
    acc[key].items.push(row);
    return acc;
  }, {});

  const trades = mappings.map((m:any)=>m.trade_category)
    .filter((t:string,i:number,a:string[])=>a.indexOf(t)===i).sort();

  const tradeDotMap: Record<string,string> = {
    CIVIL:'bg-blue-500', STRUCTURAL:'bg-indigo-500', MEP:'bg-cyan-500',
    FINISHING:'bg-rose-500', ADMIN:'bg-gray-400', SAFETY:'bg-amber-500',
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{mappings.length} activity-manpower mappings</p>
        <button onClick={()=>exportCSV(mappings.map(m=>({
          ActivityCode:m.act_code,ActivityName:m.activity_name,
          SkillCode:m.skill_code,SkillName:m.skill_name,Trade:m.trade_category,
          WorkersPerUnit:m.workers_per_unit,GangBased:m.is_gang_based?'Yes':'No',
          GangSize:m.gang_size||'',Supervision:m.supervision_required?'Yes':'No',
          DailyRate:m.current_daily_rate||'',Mandatory:m.is_mandatory?'Yes':'No'
        })),'manpower_map.csv')}
          className="flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">
          <Download size={14}/> Export</button>
      </div>
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search activity or skill..."
            className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"/>
        </div>
        <select value={tradeFilter} onChange={e=>setTrade(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Trades</option>
          {trades.map(t=><option key={t as string}>{t as string}</option>)}
        </select>
      </div>
      {loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_,i)=>(
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
              <div className="bg-gray-100 rounded h-4 w-48 mb-2"/><div className="bg-gray-100 rounded h-3 w-32"/>
            </div>
          ))}
        </div>
      ) : Object.keys(grouped).length===0 ? (
        <div className="text-center py-12 text-gray-400">No mappings found.</div>
      ) : (
        <div className="space-y-2">
          {Object.values(grouped).map((grp:any)=>(
            <div key={grp.act_code} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded mr-2">{grp.act_code}</span>
                  <span className="text-sm font-medium text-gray-900">{grp.activity_name}</span>
                </div>
                <span className="text-xs text-gray-400">{grp.items.length} skill{grp.items.length>1?'s':''}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {grp.items.map((m:any,i:number)=>(
                  <div key={i} className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs border
                    ${m.is_mandatory?'bg-red-50 border-red-200':m.is_gang_based?'bg-purple-50 border-purple-200':'bg-gray-50 border-gray-200'}`}>
                    <span className={`w-2 h-2 rounded-full ${tradeDotMap[m.trade_category]||'bg-gray-400'}`}/>
                    <span className="font-medium text-gray-800">{m.skill_name}</span>
                    <span className="text-gray-500">{m.workers_per_unit}/unit</span>
                    {m.is_gang_based && <span className="text-purple-600">gang:{m.gang_size}</span>}
                    {m.current_daily_rate && <span className="text-green-600">₹{fmtN(m.current_daily_rate)}/day</span>}
                    {m.supervision_required && <span className="text-amber-600">sup</span>}
                    {m.is_mandatory && <span className="text-red-600 font-bold">*</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// ── Summary Tab ───────────────────────────────────────────────
const SummaryTab = () => {
  const [stats, setStats]     = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    hrService.getStats().then(r=>{ setStats(r.data.data); setLoading(false); });
  }, []);
  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;
  if (!stats) return null;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label:'Total Active',    value:fmtN(stats.total_active),  color:'bg-blue-600 text-white' },
          { label:'Permanent',       value:fmtN(stats.permanent),     color:'bg-green-600 text-white' },
          { label:'Contract',        value:fmtN(stats.contract),      color:'bg-amber-500 text-white' },
          { label:'Skills / Trades', value:fmtN(stats.total_skills),  color:'bg-purple-600 text-white' },
        ].map(c=>(
          <div key={c.label} className={`${c.color} rounded-xl p-4 text-center`}>
            <p className="text-2xl font-bold">{c.value}</p>
            <p className="text-xs font-medium mt-1 opacity-90">{c.label}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Employment Breakdown</h3>
          <div className="space-y-2">
            {[
              { label:'Permanent', val:stats.permanent, color:'bg-green-500' },
              { label:'Contract',  val:stats.contract,  color:'bg-amber-500' },
              { label:'Probation', val:stats.probation, color:'bg-blue-500' },
              { label:'Intern',    val:stats.intern,    color:'bg-gray-400' },
            ].map(s=>{
              const pct = stats.total_active>0 ? (Number(s.val)/Number(stats.total_active))*100 : 0;
              return (
                <div key={s.label} className="flex items-center gap-3">
                  <span className="text-xs text-gray-600 w-20">{s.label}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-2">
                    <div className={`${s.color} h-2 rounded-full`} style={{width:`${pct}%`}}/>
                  </div>
                  <span className="text-xs font-semibold text-gray-700 w-6 text-right">{fmtN(s.val)}</span>
                </div>
              );
            })}
          </div>
        </div>
        {stats.by_department && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-700 mb-3">Headcount by Department</h3>
            <div className="space-y-2">
              {Object.entries(stats.by_department).sort(([,a]:any,[,b]:any)=>b-a).map(([dept,cnt]:any)=>(
                <div key={dept} className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium w-24 text-center ${DEPT_COLORS[dept]||'bg-gray-100 text-gray-700'}`}>{dept}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full" style={{width:`${(cnt/Number(stats.total_active))*100}%`}}/>
                  </div>
                  <span className="text-xs font-semibold text-gray-700 w-6 text-right">{cnt}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-purple-700">{fmtN(stats.total_skills)}</p>
          <p className="text-xs text-gray-500 mt-1">Skill / Labour Categories</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-blue-700">{fmtN(stats.total_subcontractors)}</p>
          <p className="text-xs text-gray-500 mt-1">Subcontractors</p>
        </div>
      </div>
    </div>
  );
};

// ── Main HRPage ───────────────────────────────────────────────
const HRPage = () => {
  const [tab, setTab] = useState<'employees'|'skills'|'subcontractors'|'manpower'|'summary'>('employees');
  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">HR & Employees</h1>
        <p className="text-sm text-gray-500 mt-0.5">Employees · Skills · Subcontractors · Manpower Mapping</p>
      </div>
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit mb-6">
        {([
          { key:'employees',    label:'Employees',        icon:<Users size={14}/> },
          { key:'skills',       label:'Skills',           icon:<BookOpen size={14}/> },
          { key:'subcontractors',label:'Subcontractors',  icon:<Briefcase size={14}/> },
          { key:'manpower',     label:'Manpower Mapping', icon:<Activity size={14}/> },
          { key:'summary',      label:'Summary',          icon:<BarChart2 size={14}/> },
        ] as const).map(t=>(
          <button key={t.key} onClick={()=>setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition-colors
              ${tab===t.key?'bg-white text-gray-900 shadow-sm':'text-gray-500 hover:text-gray-700'}`}>
            {t.icon}{t.label}</button>
        ))}
      </div>
      {tab==='employees'      && <EmployeesTab/>}
      {tab==='skills'         && <SkillsTab/>}
      {tab==='subcontractors' && <SubcontractorsTab/>}
      {tab==='manpower'       && <ManpowerMappingTab/>}
      {tab==='summary'        && <SummaryTab/>}
    </div>
  );
};

export default HRPage;
